import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FACET_SECTION_FLASH_MS } from "../components/UIToolkit/Filters/FacetFilterSection";
import type { FacetSpec } from "../components/UIToolkit/Filters/facetSections";
import { applyFacetFilters, countActiveFacets, type FacetDimension, type FacetFilterResult, type FacetSelection } from "../utils/facetFilters";

const EMPTY_SELECTION: FacetSelection = {};

export type FacetFiltersState<T> = {
	selection: FacetSelection;
	result: FacetFilterResult<T>;
	toggle: (dimensionId: string, optionId: string, checked: boolean) => void;
	/** Empties every group, the shared ones included — the rail's "Clear all". */
	clear: () => void;
	/** Empties only this grid's own groups and never changes identity — for resetting on a context change. */
	clearLocal: () => void;
	activeCount: number;
	/** The group a column's "Filter by…" just pointed at. */
	highlightedId: string | null;
	highlight: (dimensionId: string) => void;
};

/** A facet group whose selection lives outside the grid (in the URL, say), so every grid showing it shares it. */
export type SharedFacetGroup = {
	id: string;
	selected: ReadonlySet<string>;
	onChange: (selected: ReadonlySet<string>) => void;
};

// DEFAULT: no shared groups — every group's selection is local to its grid, as it always was.
const NO_SHARED_GROUPS: readonly SharedFacetGroup[] = [];

/**
 * Client-side facet filtering for a grid. Keep `predicate` and `sharedGroups` referentially stable.
 * A shared group reads and writes its own selection; Clear empties it along with the local groups.
 */
export function useFacetFilters<T>(
	rows: readonly T[],
	dimensions: readonly FacetDimension<T>[],
	predicate: (row: T) => boolean,
	sharedGroups: readonly SharedFacetGroup[] = NO_SHARED_GROUPS,
): FacetFiltersState<T> {
	const [localSelection, setLocalSelection] = useState<FacetSelection>(EMPTY_SELECTION);
	const [highlightedId, setHighlightedId] = useState<string | null>(null);
	const highlightTimer = useRef<number | null>(null);

	const selection = useMemo<FacetSelection>(
		() => (sharedGroups.length === 0 ? localSelection : { ...localSelection, ...Object.fromEntries(sharedGroups.map((group) => [group.id, group.selected])) }),
		[localSelection, sharedGroups],
	);

	const toggle = useCallback(
		(dimensionId: string, optionId: string, checked: boolean) => {
			const shared = sharedGroups.find((group) => group.id === dimensionId);
			if (shared) {
				const next = new Set(shared.selected);
				if (checked) next.add(optionId);
				else next.delete(optionId);
				shared.onChange(next);
				return;
			}
			setLocalSelection((prev) => {
				const next = new Set(prev[dimensionId]);
				if (checked) next.add(optionId);
				else next.delete(optionId);
				return { ...prev, [dimensionId]: next };
			});
		},
		[sharedGroups],
	);

	const clearLocal = useCallback(() => setLocalSelection(EMPTY_SELECTION), []);

	const clear = useCallback(() => {
		clearLocal();
		for (const group of sharedGroups) if (group.selected.size > 0) group.onChange(new Set());
	}, [clearLocal, sharedGroups]);

	const highlight = useCallback((dimensionId: string) => {
		setHighlightedId(dimensionId);
		if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
		highlightTimer.current = window.setTimeout(() => setHighlightedId(null), FACET_SECTION_FLASH_MS);
	}, []);

	useEffect(
		() => () => {
			if (highlightTimer.current) window.clearTimeout(highlightTimer.current);
		},
		[],
	);

	const result = useMemo(() => applyFacetFilters(rows, dimensions, selection, predicate), [rows, dimensions, selection, predicate]);
	const activeCount = useMemo(() => countActiveFacets(selection), [selection]);

	return { selection, result, toggle, clear, clearLocal, activeCount, highlightedId, highlight };
}

const keepEveryRow = (): boolean => true;

/** `useFacetFilters` for a grid narrowed only by its facet groups. */
export function useGridFacetFilters<T>(
	rows: readonly T[],
	facets: readonly FacetSpec<T>[],
	sharedGroups: readonly SharedFacetGroup[] = NO_SHARED_GROUPS,
): FacetFiltersState<T> {
	const dimensions = useMemo(() => facets.map((facet) => facet.dimension), [facets]);
	return useFacetFilters(rows, dimensions, keepEveryRow, sharedGroups);
}

export type FilterRailHandoff = {
	railCollapsed: boolean;
	setRailCollapsed: (collapsed: boolean) => void;
	canFilterByColumn: (field: string) => boolean;
	onFilterByColumn: (field: string) => void;
	filterByHint: (field: string) => string | undefined;
};

/** Rail collapse state plus a grid column menu's "Filter by…" hand-off: reopen the rail and flash the group. */
export function useFilterRailHandoff(
	highlight: (dimensionId: string) => void,
	columnFacet: Readonly<Partial<Record<string, string>>>,
	hint: string,
): FilterRailHandoff {
	const [railCollapsed, setRailCollapsed] = useState(false);
	const canFilterByColumn = useCallback((field: string) => columnFacet[field] != null, [columnFacet]);
	const onFilterByColumn = useCallback(
		(field: string) => {
			const facet = columnFacet[field];
			if (!facet) return;
			setRailCollapsed(false);
			highlight(facet);
		},
		[columnFacet, highlight],
	);
	const filterByHint = useCallback((field: string) => (columnFacet[field] ? hint : undefined), [columnFacet, hint]);
	return { railCollapsed, setRailCollapsed, canFilterByColumn, onFilterByColumn, filterByHint };
}
